import ProjectTabCanvas from './ProjectTabCanvas.tsx';
import ProjectTabScene from './ProjectTabScene.tsx';
import ProjectTabWebsite from './ProjectTabWebsite.tsx';
import ProjectTabContent from './ProjectTabContent.tsx';
import {
    MixModel,
    ProjectDataIndex,
    ProjectTab,
    ProjectTabType,
    TemplateContentConfig
} from '../../../api/types.ts';
import ProjectTabVibeCode from './ProjectTabVibeCode.tsx';
import ProjectTabBlocks from './ProjectTabBlocks.tsx';
import FocusModeShell from '../../shared/FocusModeShell.tsx';

interface ProjectTabPaneProps {
    mix: MixModel;
    saveProject?: (project: MixModel) => Promise<void>;
    tab: ProjectTab;
    dataIndex: ProjectDataIndex;
    readOnly: boolean;
    reactionsEnabled: boolean;
    isFocusMode?: boolean;
    onEnterFocusMode?: () => void;
    onExitFocusMode?: () => void;
    templateConfig?: TemplateContentConfig | null;
    autoOpenVibe?: boolean;
    // With autoOpenVibe on a copy: translate it into this language.
    autoVibeLanguage?: string | null;
    onVibeAutoOpened?: () => void;
}

const ProjectTabPane: React.FC<ProjectTabPaneProps> = ({
    mix,
    saveProject,
    tab,
    dataIndex,
    readOnly,
    reactionsEnabled,
    isFocusMode,
    onEnterFocusMode,
    onExitFocusMode,
    templateConfig,
    autoOpenVibe,
    autoVibeLanguage,
    onVibeAutoOpened
}) => {
    switch (tab.type) {
        case ProjectTabType.Whiteboard:
            return (
                <ProjectTabCanvas
                    mix={mix}
                    tab={tab}
                    dataIndex={dataIndex}
                    readOnly={readOnly}
                    reactionsEnabled={reactionsEnabled}
                    isFocusMode={isFocusMode}
                    onExitFocusMode={onExitFocusMode}
                />
            );
        case ProjectTabType.Scene:
            return (
                <FocusModeShell
                    isFocusMode={isFocusMode}
                    onExit={onExitFocusMode}
                    variant="scene"
                    exitLabel={readOnly ? undefined : 'Back to editor'}
                >
                    <ProjectTabScene
                        mix={mix}
                        tab={tab}
                        dataIndex={dataIndex}
                        readOnly={readOnly}
                    />
                </FocusModeShell>
            );
        case ProjectTabType.VibeCode:
            return (
                <ProjectTabVibeCode
                    mix={mix}
                    tab={tab}
                    dataIndex={dataIndex}
                    readOnly={readOnly}
                    isFocusMode={isFocusMode}
                    onExitFocusMode={onExitFocusMode}
                    autoOpenVibe={autoOpenVibe}
                    autoVibeLanguage={autoVibeLanguage}
                    onVibeAutoOpened={onVibeAutoOpened}
                />
            );
        case ProjectTabType.Blocks:
            return (
                <ProjectTabBlocks
                    mix={mix}
                    tab={tab}
                    dataIndex={dataIndex}
                    readOnly={readOnly}
                    isFocusMode={isFocusMode}
                    onEnterFocusMode={onEnterFocusMode}
                    onExitFocusMode={onExitFocusMode}
                />
            );
        case ProjectTabType.Website:
            return (
                <FocusModeShell
                    isFocusMode={isFocusMode}
                    onExit={onExitFocusMode}
                    variant="website"
                >
                    <ProjectTabWebsite
                        mix={mix}
                        saveProject={saveProject}
                        tab={tab}
                        dataIndex={dataIndex}
                        readOnly={readOnly}
                    />
                </FocusModeShell>
            );
        default:
            return (
                <FocusModeShell
                    isFocusMode={isFocusMode}
                    onExit={onExitFocusMode}
                    variant="content"
                >
                    <ProjectTabContent
                        mix={mix}
                        tab={tab}
                        dataIndex={dataIndex}
                        readOnly={readOnly}
                        templateConfig={templateConfig}
                    />
                </FocusModeShell>
            );
    }
};

export default ProjectTabPane;
